/**
 * La MARCA con la que un call site declara, en la config de axios, "de ESTA
 * peticion espero ESTA respuesta de negocio".
 *
 * Existe porque hay un caso —el unico, y esta tabulado en
 * `expectedBusinessResponses.ts` como R1— en el que el flujo sano y un bug
 * producen una peticion y una respuesta IDENTICAS: mismo metodo, misma ruta,
 * mismo status, mismo cuerpo y los dos sin `force`. Cuando el error ya no
 * distingue, lo unico honesto que queda es la PROCEDENCIA: que codigo lanzo la
 * llamada y por tanto quien se hace responsable de manejar la respuesta.
 *
 * Por que una marca y no `window.location.pathname`, que fue el primer intento:
 * la pantalla se lee en el instante en que se DECIDE, no en el que se pidio, y
 * eso resulto ser otra cosa. En MB-APP-EXPRESS-EV el bucket de `url` mas grande
 * del issue (41 eventos en 90d, frente a 12 del siguiente) no es
 * `/contracts/<folio>/create` sino el dashboard pelado `/contracts`: la
 * respuesta llega cuando el usuario ya navego —o react-query reintenta con
 * `retry: 1` despues de la navegacion— y la pantalla que se lee ya es otra. Una
 * regla atada a la pantalla deja pasar justo esos eventos. La marca viaja CON la
 * peticion, asi que no la afecta ni la navegacion, ni el reintento, ni que
 * alguien renombre una ruta de la SPA.
 *
 * Direccion del fallo: si la marca se pierde, la regla no casa y el error SE
 * CAPTURA (vuelve el ruido, nunca se esconde un bug). Es el mismo sesgo que el
 * resto del modulo.
 */

/**
 * Clave con la que la marca viaja dentro de `config`.
 *
 * Va en el cuerpo de la config y NO en `config.headers` a proposito: una
 * cabecera se enviaria al backend. Verificado de verdad (no por lectura de la
 * doc): axios conserva las claves desconocidas de la config a traves de
 * `mergeConfig`, del interceptor de peticion y del adapter, y las devuelve en
 * `error.config` — hay un test que lo afirma extremo a extremo con la instancia
 * real.
 *
 * El doble guion bajo es la señal de "esto es interno del cliente": si alguna
 * vez apareciera en un payload seria evidente que algo lo serializo por error.
 */
export const EXPECTED_BUSINESS_RESPONSE_CONFIG_KEY =
  "__expectedBusinessResponse";

/**
 * Las marcas que existen, que son exactamente los ids de las reglas que exigen
 * una.
 *
 * Es un tipo cerrado y no `string` para que una marca mal escrita en un call
 * site sea un error de compilacion y no un silencio que nadie nota: si la marca
 * no casa con la que pide la regla, el error se captura igual que antes y el
 * ruido vuelve sin aviso.
 */
export type ExpectedBusinessResponseMark =
  "editor-data-folio-already-has-contract";

/**
 * Marca la config (y la devuelve, para poder encadenar en el call site).
 *
 * Muta el objeto que recibe en vez de clonarlo porque el call site acaba de
 * construirlo; clonar obligaria a volver a tipar `params`, `headers` y el resto
 * sin ganar nada.
 */
export function markExpectedBusinessResponse<T extends object>(
  config: T,
  mark: ExpectedBusinessResponseMark,
): T {
  (config as Record<string, unknown>)[EXPECTED_BUSINESS_RESPONSE_CONFIG_KEY] =
    mark;
  return config;
}

/**
 * La marca de una config, o `""` si no la trae.
 *
 * Defensivo por el mismo motivo que todo este camino: corre dentro de
 * `beforeSend`, donde una excepcion no abriria un issue, los apagaria TODOS en
 * silencio. `config` puede ser cualquier cosa (un error no-axios, una config
 * construida a mano en un test, un objeto con un getter que lanza), asi que ante
 * cualquier duda se devuelve `""` = "sin marca" = capturar.
 */
export function readExpectedBusinessResponseMark(config: unknown): string {
  try {
    if (typeof config !== "object" || config === null) {
      return "";
    }

    const mark = (config as Record<string, unknown>)[
      EXPECTED_BUSINESS_RESPONSE_CONFIG_KEY
    ];

    return typeof mark === "string" ? mark : "";
  } catch {
    return "";
  }
}
